import { useEffect, useState } from "react";
import { publicSend, publicSubscribe, publicForget, onPublicDisconnect } from "@/lib/derivPublic";
import { SYMBOL, lastDigit } from "@/lib/evenOdd";

/** Live last-digit feed for the EXACT market being traded. Bootstraps recent
 * history, then streams ticks. Re-subscribes when the locked market changes
 * so the tape never shows a different pair than the one contracts fire on.
 * Self-heals on socket drops. Public WS — no auth. */
export const useDigitTape = (max = 100, symbol = SYMBOL) => {
  const [state, setState] = useState({ digits: [], total: 0 });

  useEffect(() => {
    let alive = true;
    let subId = null;
    setState({ digits: [], total: 0 }); // fresh tape for the new market
    const start = async () => {
      try {
        const h = await publicSend({ ticks_history: symbol, count: 40, end: "latest", style: "ticks" });
        const prices = h?.history?.prices || [];
        if (alive && prices.length) {
          setState({ digits: prices.slice(-max).map((p) => lastDigit(p)), total: prices.length });
        }
      } catch { /* live subscribe below still fills the tape */ }
      try {
        const { subscription_id } = await publicSubscribe({ ticks: symbol, subscribe: 1 }, (msg) => {
          const q = msg?.tick?.quote;
          if (q == null || !alive) return;
          const d = lastDigit(parseFloat(q));
          if (!Number.isFinite(d)) return;
          setState((s) => ({ digits: [...s.digits.slice(-(max - 1)), d], total: s.total + 1 }));
        });
        if (!alive) { publicForget(subscription_id); return; }
        subId = subscription_id;
      } catch { /* rate-limited subscribe — history snapshot still shows */ }
    };
    start();
    const off = onPublicDisconnect(() => { if (alive) start(); });
    return () => {
      alive = false;
      if (subId) publicForget(subId);
      if (typeof off === "function") off();
    };
  }, [max, symbol]);

  return state;
};
